import { useMemo } from 'react';
import { aplicarNegativoNoFinal, descontoEsim, escopoEsimDoGestor } from '../../../../backend/src/domain/qualidade/produtividade-ativacao';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useMesFechado } from './useMesFechado';
import {
  calcularFaturamentoPorCategoria,
  useCalculoComissao,
  FaturamentoPorCategoria,
  QuantidadesMetas
} from './useCalculoComissao';
import { useCargoNoMes } from './useCargoNoMes';
import { useEmExperiencia } from './useEmExperiencia';
import { useProdutosCategorias } from './useProdutosCategorias';
import { contaParaMetaN, ativoNaCompetencia, ajudaNaMetaDoGestor, consultoresDoSegmentoDoGestor } from './regrasContagem';
import { outrosSegmentosDoGestor } from '../../../../backend/src/domain/comissao/gestorOutroSegmento';
import { useMetaAgregadaGestor } from './useMetaAgregadaGestor';
import { useComissaoPerdidaConvergencia } from './useComissaoPerdidaConvergencia';
import { useConvergenciaSaldoLote } from './useConvergenciaSaldoLote';
import { useDeflatorCancelamentoLote } from './useDeflatorCancelamentoLote';
import { removerEstornoNaoVigente } from './estornoVigencia';
import { useComissaoBundleVisao } from './useComissaoBundleVisao';
import { useSegmentosDasEquipes } from './useCommissionRules';
import { format } from 'date-fns';

interface ItemPlanilha {
  item_id?: string | null;
  pedido_id?: string | null;
  consultor_id?: string;
  fora_sul?: boolean | null;
  produto: string;
  quantidade: number;
  subtotal: number;
  status_categoria: string;
  data_ativacao?: string | null;
  data_venda?: string | null;
  quadro_atual?: string | null;
  coluna_atual?: string | null;
  linhas_telefone?: unknown;
  /** false = venda de outro segmento: paga, mas não conta na meta. */
  pontua_segmento?: boolean | null;
}

const CARGOS_EQUIPE = ['SUPERVISOR', 'COORDENADOR', 'GERENTE'];

function mapCargoToComissao(role: string): 'CONSULTOR' | 'SUPERVISOR' | 'COORDENADOR' | 'GERENTE' {
  const upper = role?.toUpperCase() || 'CONSULTOR';
  if (upper === 'SUPERVISOR' || upper.includes('SUPERVISOR')) return 'SUPERVISOR';
  if (upper === 'COORDENADOR' || upper.includes('COORDENADOR')) return 'COORDENADOR';
  if (upper === 'GERENTE' || upper.includes('GERENTE') || upper === 'DIRETOR' || upper === 'MASTER') return 'GERENTE';
  return 'CONSULTOR';
}

// Consultores subordinados de um userId — agora derivado do BUNDLE (sem rede).
function useConsultoresDoUsuario(userId: string | undefined, competencia?: Date) {
  const { data: bundle } = useComissaoBundleVisao(competencia ?? new Date());
  const data = useMemo(() => {
    if (!userId || !bundle) return undefined;
    const linhas = (bundle.hierarquias[userId] || []) as any[];
    const inicioMesIso = `${bundle.competencia}-01`;
    const uMap = new Map(bundle.usuarios.map((u) => [u.id, u]));
    return linhas.map((c) => ({
      ...c,
      ativo: ativoNaCompetencia((uMap.get(c.consultor_id) || {}) as any, inicioMesIso) !== false,
    }));
  }, [userId, bundle]);
  return { data, isLoading: !bundle };
}

// Itens do mês — derivado do BUNDLE (a tela navegava consultor a consultor
// refazendo fetch paginado; agora é filtro em memória).
function useItensMesTrabalho(
  userId: string | undefined,
  competencia: Date,
  consultorIds: string[] | null,
  aguardandoHierarquia = false
) {
  const mesTrabalho = format(competencia, 'yyyy-MM');
  const { data: bundle, isLoading: bundleLoading } = useComissaoBundleVisao(competencia);
  const data = useMemo(() => {
    if (!userId || !bundle || aguardandoHierarquia) return undefined;
    const alvo = consultorIds && consultorIds.length > 0 ? new Set(consultorIds) : new Set([userId]);
    const doEscopo = (bundle.itens as ItemPlanilha[]).filter(
      (i) => i.consultor_id && alvo.has(i.consultor_id)
        // Pedido irregular (Contato Real com número da casa) não paga (Douglas 28/09/2026).
        && !(i as ItemPlanilha & { nao_paga_comercial?: boolean }).nao_paga_comercial
    );
    return removerEstornoNaoVigente(doEscopo, mesTrabalho);
  }, [userId, bundle, consultorIds, aguardandoHierarquia, mesTrabalho]);
  return { data, isLoading: bundleLoading || (!!userId && data === undefined) };
}

export interface OpcoesVisaoComissaoSimulada {
  /**
   * MODO LEVE (Douglas 29/07 — "ainda muito lento para carregar página").
   *
   * O <Select> de pessoas renderiza um `SelectItemFuncionario` por funcionário,
   * e cada um chamava este hook por inteiro. O peso não é o cálculo — é o
   * `useComissaoPerdidaConvergencia`, que faz ~8 idas ao banco POR PESSOA,
   * incluindo a `v_convergencia_oportunidade` (~700ms). Com ~40 consultores no
   * seletor isso virava centenas de requisições só pra montar a lista.
   *
   * No modo leve a balança vem de `useConvergenciaSaldoLote`: UMA query
   * compartilhada (mesma chave do React Query) resolve o saldo de todo mundo.
   * A CONTA É A MESMA — perda = fat penalizável × %, ganho = fat bonificável ×
   * % (só se bateu meta), mesmas views, mesmo carry-forward de competência.
   * Muda só de onde os números vêm.
   *
   * O card principal NÃO usa modo leve: ele precisa das listas por pedido/
   * cliente pro painel de convergência, que só o hook detalhado devolve.
   */
  leve?: boolean;
  /**
   * Gestor com equipe nos dois segmentos (de 2026-10, Douglas 21/10/2026): qual
   * lado calcular. Sem isto, o segmento da equipe da pessoa (o lado de sempre).
   */
  segmento?: 'PF' | 'PJ';
}

export function useVisaoComissaoSimulada(
  userId: string | undefined,
  role: string,
  competencia: Date,
  opcoes?: OpcoesVisaoComissaoSimulada,
) {
  const leve = opcoes?.leve === true;
  const { cargo: cargoHistorico } = useCargoNoMes(userId, competencia, role);
  const cargo = mapCargoToComissao(cargoHistorico);
  const isEquipe = CARGOS_EQUIPE.includes(cargo);
  const { data: bundleAtivacao } = useComissaoBundleVisao(competencia);

  // 🔒 Mês fechado: a tela passa a LER a fotografia gravada no fechamento
  // (snapshot_payload.tela em commission_result_monthly) — edições posteriores
  // na planilha/regras não mudam mais o que este mês exibe. Fechamentos antigos
  // sem fotografia caem no cálculo dinâmico normal.
  const mesIsoSnap = `${format(competencia, 'yyyy-MM')}-01`;
  const { data: mesStatus } = useMesFechado(competencia);
  const mesFechado = mesStatus?.fechado === true;
  const { data: snapTela, isLoading: snapLoading } = useQuery({
    queryKey: ['comissao-snapshot-tela', userId, mesIsoSnap],
    enabled: mesFechado && !!userId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('commission_result_monthly' as never)
        .select('snapshot_payload')
        .eq('pessoa_id', userId)
        .eq('competencia', mesIsoSnap)
        .limit(1);
      if (error) throw error;
      const payload = (data as Array<{ snapshot_payload: Record<string, unknown> | null }> | null)?.[0]?.snapshot_payload;
      return (payload?.tela ?? null) as Record<string, unknown> | null;
    },
  });

  // 🎓 Período de experiência — somente CONSULTOR (simulado pelo Master)
  const { emExperiencia } = useEmExperiencia(
    cargo === 'CONSULTOR' ? userId : undefined,
    competencia
  );

  const { data: consultoresHierarquia } = useConsultoresDoUsuario(isEquipe ? userId : undefined, competencia);
  // O gestor é medido só pelos consultores do segmento dele (CPF e CNPJ nunca
  // somam) — mesma regra da folha e do Excel (consultoresDoSegmentoDoGestor).
  const segmentosEquipes = useSegmentosDasEquipes();
  const equipeDaPessoa = bundleAtivacao?.usuarios.find((u) => u.id === userId)?.equipe_id ?? null;
  const segmentoDaPessoa: 'PF' | 'PJ' = (equipeDaPessoa && segmentosEquipes?.porId.get(equipeDaPessoa)) || 'PJ';
  // Gestor com equipe nos dois segmentos (de 2026-10): os lados que ele tem.
  const outrosSegmentos = useMemo(() => (isEquipe && consultoresHierarquia && segmentosEquipes
    ? outrosSegmentosDoGestor(format(competencia, 'yyyy-MM'), segmentoDaPessoa, consultoresHierarquia, segmentosEquipes.porNome)
    : []), [isEquipe, consultoresHierarquia, segmentosEquipes, segmentoDaPessoa, competencia]);
  const segmentoCalculo: 'PF' | 'PJ' = opcoes?.segmento && outrosSegmentos.includes(opcoes.segmento) ? opcoes.segmento : segmentoDaPessoa;
  const outroLado = segmentoCalculo !== segmentoDaPessoa;
  const consultores = useMemo(() => {
    if (!consultoresHierarquia || !segmentosEquipes) return undefined;
    return consultoresDoSegmentoDoGestor(consultoresHierarquia, segmentoCalculo, segmentosEquipes.porNome);
  }, [consultoresHierarquia, segmentosEquipes, segmentoCalculo]);

  // Mapa dinâmico de categorias (Matriz Produto x KPIs)
  const { data: catCfg } = useProdutosCategorias();
  const calcConfig = useMemo(() => ({
    categoriasMap: catCfg?.categoriasMap,
    produtosAltaNomes: catCfg?.produtosAlta,
    produtosFibraNomes: catCfg?.produtosFibra,
    produtosTtNomes: catCfg?.produtosTt,
    produtosMpNomes: catCfg?.produtosMp,
    produtosMigracaoNomes: catCfg?.produtosMigracao,
    // (Douglas 28/07) Sem estas duas listas o faturamento de T.I./Avançada ficava
    // 0 na tela mesmo com venda — e o Excel, que já as passava, pagava diferente.
    produtosTiNomes: catCfg?.produtosTi,
    produtosSdNomes: catCfg?.produtosSd,
    produtosTvNomes: catCfg?.produtosTv,
    produtosAvancadaNomes: catCfg?.produtosAvancada,
  }), [catCfg]);

  // IDs para meta: subordinados (incluindo DIRETORIA).
  // Líder com venda própria já vem na RPC como vendedor normal (is_lideranca=false).
  // Equipe QUALIDADE não entra no cálculo de meta (não é consultor).
  const consultorIdsMeta = useMemo(() => {
    if (!isEquipe || !consultores) return null;
    return consultores
      .filter(c => ajudaNaMetaDoGestor(c))
      .map(c => c.consultor_id);
  }, [isEquipe, consultores]);

  // Set de IDs apenas da DIRETORIA (excluídos da comissão).
  // Liderança não é mais excluída: líder com venda conta normal como vendedor.
  const diretoriaIdSet = useMemo(() => {
    if (!isEquipe || !consultores) return new Set<string>();
    return new Set(consultores.filter(c => c.da_diretoria).map(c => c.consultor_id));
  }, [isEquipe, consultores]);

  // Busca todos os itens (inclui DIRETORIA) — depois dividimos localmente
  const { data: todosItens, isLoading: itensLoading } = useItensMesTrabalho(userId, competencia, consultorIdsMeta, isEquipe && !consultores);

  // nVendedores: conta direto da RPC via helper único `contaParaMetaN`.
  // Mesma regra aplicada em useMinhaComissao e VendedoresEquipePanel —
  // garante que o número do header bate com o do painel da equipe.
  const nVendedores = useMemo(() => {
    if (!isEquipe || !consultores) return 0;
    return consultores.filter(contaParaMetaN).length;
  }, [isEquipe, consultores]);

  // Separar ativo e pendente; e comissao vs meta
  const { itensAtivos, itensPendentes } = useMemo(() => {
    if (!todosItens) return { itensAtivos: [], itensPendentes: [] };
    // itensAtivos para comissão: sem DIRETORIA, só QUALIDADE
    const itensAtivos = todosItens.filter(
      i => i.quadro_atual === 'QUALIDADE' && !diretoriaIdSet.has(i.consultor_id || '')
    );
    return {
      itensAtivos,
      itensPendentes: todosItens.filter(i => i.quadro_atual !== 'QUALIDADE'),
    };
  }, [todosItens, diretoriaIdSet]);

  // A lista "pedidos e a regra de cada um" (Douglas 22/10/2026): os itens que
  // pagam a pessoa (ativos e pendentes), sem a DIRETORIA, e o nome de quem vendeu.
  const itensPedidos = useMemo(
    () => (todosItens || []).filter((i) => !diretoriaIdSet.has(i.consultor_id || '')),
    [todosItens, diretoriaIdSet],
  );
  const nomesConsultores = useMemo(
    () => new Map((consultores || []).map((c) => [c.consultor_id, c.consultor_nome as string | null])),
    [consultores],
  );

  // Itens META (inclui DIRETORIA, só QUALIDADE) — para bateuMeta. Venda de
  // outro segmento (CNPJ de quem é do CPF) paga, mas não conta na meta
  // (Douglas 21/10/2026) — mesma régua da folha e do Excel.
  const pontuaNaMeta = (i: { pontua_segmento?: boolean | null }) => i.pontua_segmento !== false;
  const itensAtivosMeta = useMemo(() => {
    if (!todosItens) return [];
    return todosItens.filter(i => i.quadro_atual === 'QUALIDADE' && pontuaNaMeta(i));
  }, [todosItens]);

  const { faturamentos, quantidades } = useMemo(() => {
    if (!itensAtivos || itensAtivos.length === 0) {
      return {
        faturamentos: { pn: 0, ha: 0, fibra: 0, tt: 0, ttpre: 0, mp: 0, ti: 0, avancada: 0, total: 0 } as FaturamentoPorCategoria,
        quantidades: { altas: 0, fibras: 0, tt: 0, mp: 0, migracao: 0, sd: 0 } as QuantidadesMetas,
      };
    }
    return calcularFaturamentoPorCategoria(itensAtivos, calcConfig);
  }, [itensAtivos, calcConfig]);

  // Faturamentos/quantidades META (inclui DIRETORIA) para bateuMeta
  const { faturamentosMeta, quantidadesMeta } = useMemo(() => {
    if (!itensAtivosMeta || itensAtivosMeta.length === 0) {
      return {
        faturamentosMeta: { pn: 0, ha: 0, fibra: 0, tt: 0, ttpre: 0, mp: 0, ti: 0, avancada: 0, total: 0 } as FaturamentoPorCategoria,
        quantidadesMeta: { altas: 0, fibras: 0, tt: 0, mp: 0, migracao: 0, sd: 0 } as QuantidadesMetas,
      };
    }
    const calc = calcularFaturamentoPorCategoria(itensAtivosMeta, calcConfig);
    return { faturamentosMeta: calc.faturamentos, quantidadesMeta: calc.quantidades };
  }, [itensAtivosMeta, calcConfig]);

  // Estorno por categoria (deflator = itens QUALIDADE na coluna ESTORNO, qtd negativa).
  // Já está embutido em quantidadesMeta; aqui isolamos só pra EXIBIR no card.
  const quantidadesEstorno = useMemo<QuantidadesMetas>(() => {
    const estItens = (itensAtivosMeta || []).filter(i => (i.coluna_atual || '') === 'ESTORNO');
    if (estItens.length === 0) {
      return { altas: 0, fibras: 0, tt: 0, mp: 0, migracao: 0, sd: 0 };
    }
    return calcularFaturamentoPorCategoria(estItens, calcConfig).quantidades;
  }, [itensAtivosMeta, calcConfig]);

  const { faturamentosPendente, quantidadesPendente } = useMemo(() => {
    if (!itensPendentes || itensPendentes.length === 0) {
      return {
        faturamentosPendente: { pn: 0, ha: 0, fibra: 0, tt: 0, ttpre: 0, mp: 0, ti: 0, avancada: 0, total: 0 } as FaturamentoPorCategoria,
        quantidadesPendente: { altas: 0, fibras: 0, tt: 0, mp: 0, migracao: 0, sd: 0 } as QuantidadesMetas,
      };
    }
    // R$ pendente conta toda a venda; a quantidade pendente é dos cards de
    // meta, então só a venda do segmento.
    const calc = calcularFaturamentoPorCategoria(itensPendentes, calcConfig);
    const calcMeta = calcularFaturamentoPorCategoria(itensPendentes.filter(pontuaNaMeta), calcConfig);
    return { faturamentosPendente: calc.faturamentos, quantidadesPendente: calcMeta.quantidades };
  }, [itensPendentes, calcConfig]);

  // Para supervisores, passa o userId para buscar regra individual de comissão
  // Passa userId para buscar regras individuais: supervisores OU consultores (DIRETORIA)
  const usuarioIdComissao = (cargo === 'SUPERVISOR' || cargo === 'CONSULTOR') ? userId : undefined;

  // 🆕 Meta-alvo do gestor = soma das metas das equipes (meta da equipe × nº de
  // consultores). Só para gestores (isEquipe); consultor segue meta própria.
  const metaAgregada = useMetaAgregadaGestor(consultores, competencia, isEquipe);

  // (Douglas 29/10/2026) Regra 3 no gestor: o TT e a Renovação da Migração são
  // só das equipes, sem a Diretoria. Nas regras 1 e 2 a Diretoria ajuda.
  const quantidadesMigracao = useMemo(() => {
    if (!isEquipe) return undefined;
    const semDiretoria = (itensAtivosMeta || []).filter(i => !diretoriaIdSet.has(i.consultor_id || ''));
    return calcularFaturamentoPorCategoria(semDiretoria, calcConfig).quantidades;
  }, [isEquipe, itensAtivosMeta, diretoriaIdSet, calcConfig]);

  const { resultado, isLoading: calculoLoading } = useCalculoComissao(
    cargo,
    faturamentos,
    quantidades,
    nVendedores,
    competencia,
    quantidadesMeta,
    faturamentosMeta,
    usuarioIdComissao,
    emExperiencia,
    metaAgregada,
    itensAtivos,
    segmentoCalculo,
    quantidadesMigracao,
  );

  const nEmExperiencia = useMemo(() => {
    if (!isEquipe || !consultores) return 0;
    return consultores.filter(c => c.em_experiencia).length;
  }, [isEquipe, consultores]);

  // BALANÇA de convergência: desconta quando NÃO converge (perda) e acrescenta
  // quando converge (ganho). O ganho só vale se BATEU A META (igual a comissão
  // base). Faturamento/meta intactos. comissaoFinal nunca abaixo de 0.
  // Detalhado (card) vs lote (seletor). O `enabled` de cada um garante que só
  // UM dos dois vai à rede — os hooks precisam ser chamados sempre, por regra
  // do React, mas o desabilitado não dispara consulta.
  // O outro lado do gestor usa o saldo em lote, que respeita o escopo filtrado
  // pelo segmento; o detalhado lê a hierarquia inteira.
  const usarLote = leve || outroLado;
  const { data: perdaConv } = useComissaoPerdidaConvergencia(usarLote ? undefined : userId, competencia, cargo);
  const { data: convLote } = useConvergenciaSaldoLote(competencia, usarLote && !!userId);
  const bateuMeta = !!resultado?.bateuMeta;

  // Escopo do saldo no modo leve: gestor = hierarquia dele (mesmas linhas que a
  // RPC devolve, já vindas do bundle); consultor = ele mesmo. Idêntico ao que o
  // hook detalhado monta internamente.
  const idsEscopoLote = useMemo(() => {
    if (!usarLote || !userId) return [];
    if (isEquipe && consultores) return consultores.map((c) => c.consultor_id).filter(Boolean);
    return [userId];
  }, [usarLote, userId, isEquipe, consultores]);

  const saldoLote = usarLote && convLote
    ? convLote.saldoDe(idsEscopoLote, cargo, bateuMeta)
    : null;

  const perdaConvergencia = usarLote ? (saldoLote?.perda ?? 0) : (perdaConv?.perda ?? 0);
  const ganhoConvergencia = usarLote
    ? (saldoLote?.ganho ?? 0)   // saldoDe já zera o ganho quando não bateu meta
    : (bateuMeta ? (perdaConv?.ganho ?? 0) : 0);
  const saldoConvergencia = ganhoConvergencia - perdaConvergencia;
  const comissaoBruta = resultado ? (bateuMeta ? resultado.comissoes.total : 0) : 0;
  const comissaoAposConvergencia = Math.max(0, comissaoBruta - perdaConvergencia + ganhoConvergencia);

  // 🔻 DEFLATOR DE CANCELAMENTO (Douglas 31/07). Incide DEPOIS da balança,
  // sobre o que a pessoa efetivamente ganharia — foi assim que ele descreveu:
  // "direito a ganhar 5.000, deflator 10%, perde 500". Aplicar antes da balança
  // faria o desconto mudar de tamanho conforme a convergência, que é outro
  // assunto.
  const { data: deflatorLote } = useDeflatorCancelamentoLote(competencia, !!userId);
  const idsEscopoDeflator = useMemo(() => {
    if (!userId) return [];
    if (isEquipe && consultores) return consultores.map((c) => c.consultor_id).filter(Boolean);
    return [userId];
  }, [userId, isEquipe, consultores]);
  const deflator = deflatorLote
    ? deflatorLote.calcular(idsEscopoDeflator, cargo, comissaoAposConvergencia)
    : null;
  const descontoCancelamento = deflator?.desconto ?? 0;
  const linhasAtivacao = (bundleAtivacao?.ativacao?.linhas || []).filter(l => l.consultorId === userId);
  const resumoAtivacao = { total: linhasAtivacao.length, validadas: linhasAtivacao.filter(l => !l.pendente).length,
    esimReal: linhasAtivacao.filter(l => l.tipo === 'esim_real').length,simcard: linhasAtivacao.filter(l => l.tipo === 'esim_simcard').length };
  // eSIM não validado (Douglas 30/09/2026): consultor, supervisor e gerente,
  // cada um com o SEU % sobre as linhas do SEU escopo. Só com a meta batida.
  const percentuaisEsim = { pn: resultado?.percentuais.pn || 0, ha: resultado?.percentuais.ha || 0 };
  const idsEscopoEsim = isEquipe && consultores ? escopoEsimDoGestor(consultores) : idsEscopoDeflator;
  const descontoAtivacao = descontoEsim(bundleAtivacao?.ativacao, {
    pessoaId: userId, idsEscopo: idsEscopoEsim, cargo, bateuMeta, percentuais: percentuaisEsim });
  const escopoEsim = new Set(idsEscopoEsim.length ? idsEscopoEsim : userId ? [userId] : []);
  const linhasEsimPendentes = (bundleAtivacao?.ativacao?.linhas || []).filter(l => l.pendente && escopoEsim.has(l.consultorId)).length;
  const prazoEsim = bundleAtivacao?.ativacao?.habilitada && !bundleAtivacao.ativacao.prazo_encerrado
    ? format(new Date(competencia.getFullYear(), competencia.getMonth() + 2, 0), 'dd/MM') : null;
  const comissaoFinalAntesAtivacao = Math.max(0, comissaoAposConvergencia - descontoCancelamento);
  const comissaoFinal = aplicarNegativoNoFinal(comissaoFinalAntesAtivacao,descontoAtivacao);

  // Mês fechado COM fotografia: devolve o congelado (mesma forma do retorno
  // dinâmico). Enquanto a fotografia carrega, mantém isLoading.
  if (mesFechado && !outroLado) {
    const t = snapTela as {
      resultado?: NonNullable<typeof resultado>;
      faturamentosPendente?: typeof faturamentosPendente;
      quantidadesPendente?: typeof quantidadesPendente;
      quantidadesEstorno?: typeof quantidadesEstorno;
      cargo?: typeof cargo;
      nVendedores?: number;
      nEmExperiencia?: number;
      perdaConvergencia?: number;
      ganhoConvergencia?: number;
      saldoConvergencia?: number;
      comissaoBruta?: number;
      comissaoFinal?: number;
      descontoAtivacao?: number;
      comissaoFinalAntesAtivacao?: number;
      resumoAtivacao?: typeof resumoAtivacao;
      deflatorCancelamento?: typeof deflator;
      descontoCancelamento?: number;
    } | null;
    if (snapLoading) {
      return {
        outrosSegmentos, segmentoCalculo, segmentoDaPessoa,
        itensPedidos, nomesConsultores,
        resultado: undefined,
        itens: [],
        faturamentosPendente,
        quantidadesPendente,
        quantidadesEstorno,
        isLoading: true,
        competencia,
        cargo,
        nVendedores: 0,
        nEmExperiencia: 0,
        perdaConvergencia: 0,
        ganhoConvergencia: 0,
        saldoConvergencia: 0,
        comissaoBruta: 0,
        comissaoFinal: 0,
        descontoAtivacao: 0,
        comissaoFinalAntesAtivacao: 0,
        resumoAtivacao,
        percentuaisEsim,
        linhasEsimPendentes: 0,
        prazoEsim: null as string | null,
        deflatorCancelamento: null,
        descontoCancelamento: 0,
        idsEscopoCancelamento: [] as string[],
        snapshotFechado: false,
      };
    }
    if (t?.resultado) {
      // A balança do cancelado aparece também no mês fechado. O desconto é o
      // que entrou na foto: a foto nova guarda o deflator; a antiga (tela-v1)
      // só guardou o valor final, e o desconto é o que falta entre a comissão
      // depois da convergência e ela. O % e as listas são os de agora.
      const descontoFoto = t.descontoCancelamento ?? Math.max(0,
        Math.max(0, (t.comissaoBruta ?? 0) - (t.perdaConvergencia ?? 0) + (t.ganhoConvergencia ?? 0))
        - (t.comissaoFinalAntesAtivacao ?? t.comissaoFinal ?? 0));
      const descontoArredondado = Math.round(descontoFoto * 100) / 100;
      const deflatorFoto = t.deflatorCancelamento
        ?? (deflator ? { ...deflator, desconto: descontoArredondado, estourou: descontoArredondado > 0 } : null);
      return {
        outrosSegmentos, segmentoCalculo, segmentoDaPessoa,
        itensPedidos, nomesConsultores,
        resultado: t.resultado,
        itens: [] as typeof itensAtivos,
        faturamentosPendente: t.faturamentosPendente ?? faturamentosPendente,
        quantidadesPendente: t.quantidadesPendente ?? quantidadesPendente,
        quantidadesEstorno: t.quantidadesEstorno ?? quantidadesEstorno,
        isLoading: false,
        competencia,
        cargo: t.cargo ?? cargo,
        nVendedores: t.nVendedores ?? 0,
        nEmExperiencia: t.nEmExperiencia ?? 0,
        perdaConvergencia: t.perdaConvergencia ?? 0,
        ganhoConvergencia: t.ganhoConvergencia ?? 0,
        saldoConvergencia: t.saldoConvergencia ?? 0,
        comissaoBruta: t.comissaoBruta ?? 0,
        comissaoFinal: t.comissaoFinal ?? 0,
        descontoAtivacao: t.descontoAtivacao ?? 0,
        comissaoFinalAntesAtivacao: t.comissaoFinalAntesAtivacao ?? t.comissaoFinal ?? 0,
        resumoAtivacao: t.resumoAtivacao ?? resumoAtivacao,
        percentuaisEsim: { pn: t.resultado.percentuais.pn || 0, ha: t.resultado.percentuais.ha || 0 },
        linhasEsimPendentes: t.resumoAtivacao ? t.resumoAtivacao.total - t.resumoAtivacao.validadas : 0,
        prazoEsim: null as string | null,
        deflatorCancelamento: deflatorFoto,
        descontoCancelamento: deflatorFoto?.desconto ?? 0,
        idsEscopoCancelamento: idsEscopoDeflator,
        snapshotFechado: true,
      };
    }
    // fechado sem fotografia (fechamentos antigos) → segue dinâmico abaixo
  }
  // Outro lado do gestor no mês fechado: o valor pago é o que entrou na foto.
  const outroLadoFoto = outroLado && mesFechado ? (snapTela as { comissaoOutroSegmento?: number } | null)?.comissaoOutroSegmento : undefined;

  return {
    outrosSegmentos, segmentoCalculo, segmentoDaPessoa,
    itensPedidos, nomesConsultores,
    resultado,
    itens: itensAtivos,
    faturamentosPendente,
    quantidadesPendente,
    quantidadesEstorno,
    isLoading: itensLoading || calculoLoading,
    competencia,
    cargo,
    nVendedores,
    nEmExperiencia,
    // 🆕 Balança de convergência: desconto (perda) + acréscimo (ganho) + saldo.
    perdaConvergencia,
    ganhoConvergencia,
    saldoConvergencia,
    comissaoBruta,
    comissaoFinal: typeof outroLadoFoto === 'number' ? outroLadoFoto : comissaoFinal,
    descontoAtivacao,
    comissaoFinalAntesAtivacao,
    resumoAtivacao,
    percentuaisEsim,
    linhasEsimPendentes,
    prazoEsim,
    // 🔻 Deflator por excesso de cancelamento
    deflatorCancelamento: deflator,
    descontoCancelamento,
    // Escopo medido pelo deflator — a balança do cancelamento lista EXATAMENTE
    // esses ids, senão a lista e o % divergiriam.
    idsEscopoCancelamento: idsEscopoDeflator,
    snapshotFechado: false,
  };
}
